import { useState, useEffect } from "react";
import API from "../../../API/API";
import Logo from "../../../JPEG/Logo-pdf.png";
import Config from "../../../API/Config";

const companyInfo = {
  name: "PREMIUM USED AUTO PARTS LLC",
  address: "10940 White rock Rd, #Suite 210",
  cityStateZip: "Rancho Cordova, CA 95670",
  phone: "1-800-606-1107",
  website: "www.premiumusedautoparts.com",
  email: "parts@premiumusedautoparts.com",
};

const normalizeContactValue = (value) => {
  if (value == null) return "";
  return String(value).trim();
};

// Builds a display name from either a populated User document (firstName/lastName,
// fullName, userName) or the sanitized user object this app stores in
// localStorage("userData") after login - which uses the same field shapes.
const getPersonDisplayName = (person) => {
  if (!person || typeof person !== "object") return "";
  return (
    person.fullName ||
    `${person.firstName || ""} ${person.lastName || ""}`.trim() ||
    person.userName ||
    ""
  );
};

const getLoggedInUser = () => {
  try {
    const raw = localStorage.getItem("userData");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function PurchaseOrderTemplate({ lead, onSendSuccess }) {
  const [yardDetails, setYardDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [selectedContactIndex, setSelectedContactIndex] = useState(0);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      file,
      previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
    e.target.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const removed = prev.find((a) => a.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type, name) => {
    if (type.startsWith("image/")) return "🖼️";
    if (type === "application/pdf") return "📄";
    if (type.includes("word") || name.endsWith(".doc") || name.endsWith(".docx")) return "📝";
    if (type.includes("excel") || name.endsWith(".xls") || name.endsWith(".xlsx")) return "📊";
    if (type.includes("zip") || type.includes("rar")) return "🗜️";
    return "📎";
  };

  useEffect(() => {
    if (lead && lead.yard && lead.yard.length > 0 && API.GetYardById) {
      fetchYardDetails();
    }
  }, [lead]);

  const fetchYardDetails = async () => {
    try {
      setLoading(true);
      const yardPromises = lead.yard.map(async (yard) => {
        if (yard.yardId) {
          const yardResponse = await API.GetYardById(yard.yardId);
          const yardData = yardResponse?.data || yardResponse;
          return { yardId: yard.yardId, details: yardData };
        }
        return null;
      });
      const yardResults = await Promise.all(yardPromises);
      const yardDetailsMap = {};
      yardResults.forEach((result) => {
        if (result) yardDetailsMap[result.yardId] = result.details;
      });
      setYardDetails(yardDetailsMap);
    } catch (err) {
      console.error("Error fetching yard details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToMail = async () => {
    try {
      setLoading(true);
      if (!lead || !lead._id) {
        alert("Lead information is missing");
        return;
      }

      // Get the selected contact email
      const selectedEmail = currentContactEmail || "parts@ecauosalvage.com";

      // Who's sending this, so the backend can stamp the status-history audit trail.
      // Login stores the user via sanitizeUser(), which returns `id`, not `_id` -
      // fall back to `_id` too in case some other login path ever stores it that way.
      const userDataString = localStorage.getItem("userData");
      const currentUser = userDataString ? JSON.parse(userDataString) : null;
      const currentUserId = currentUser?.id || currentUser?._id;
      console.log("[PurchaseOrderTemplate] currentUser from localStorage:", currentUser, "-> userId:", currentUserId);

      // Create FormData to send files
      const formData = new FormData();
      formData.append("leadId", lead._id);
      formData.append("selectedEmail", selectedEmail); // Send selected email to backend
      formData.append("selectedContactName", currentContactPerson); // Send contact name
      formData.append("selectedContactNumber", currentContactNumber); // Send contact number
      formData.append("selectedContactIndex", selectedContactIndex); // Send index if needed
      if (currentUserId) {
        formData.append("userId", currentUserId); // Who sent the PO email
      }
      formData.append("footerSignatoryName", footerSignatoryName); // Keep emailed PDF footer in sync with the on-screen preview

      // Append each file to FormData
      attachments.forEach((attachment) => {
        if (attachment.file) {
          formData.append("attachments", attachment.file);
        }
      });

      const response = await fetch(
        `${Config.baseURL}/api/emails/send-purchase-order`,
        {
          method: "POST",
          body: formData,
        }
      );
      
      const result = await response.json();
      if (result.success) {
        alert(result.message || "Purchase Order sent successfully!");
        if (onSendSuccess) onSendSuccess(result);
      } else {
        alert(result.message || "Failed to send Purchase Order");
      }
    } catch (error) {
      console.error("Error sending purchase order email:", error);
      alert("Failed to send Purchase Order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  if (!lead) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Arial, sans-serif",
          color: "#991b1b",
        }}
      >
        No lead data available
      </div>
    );
  }

  const latestYard =
    lead.yard && lead.yard.length > 0 ? lead.yard[lead.yard.length - 1] : {};
  const yardDetail = latestYard.yardId ? yardDetails[latestYard.yardId] : null;

  // Footer signatory: the Order Service Agent assigned to this order is most
  // relevant to the transaction, so prefer that when it's available (populated
  // as an object). Falls back to the currently logged-in user otherwise - e.g.
  // when opened from a list view where orderServiceHandledBy is just an id.
  const footerSignatoryName =
    getPersonDisplayName(lead.orderServiceHandledBy) ||
    getPersonDisplayName(getLoggedInUser()) ||
    "N/A";
  const assignmentContact = {
    person: normalizeContactValue(latestYard.contactPerson),
    email: normalizeContactValue(latestYard.contactEmail),
    number: normalizeContactValue(latestYard.contactNumber),
  };
  const hasAssignmentContact = Boolean(
    assignmentContact.person || assignmentContact.email || assignmentContact.number
  );

  // Set default contact index when yard details load
  useEffect(() => {
    if (hasAssignmentContact) {
      setSelectedContactIndex(-1);
      return;
    }

    if (yardDetail && yardDetail.contactPerson && yardDetail.contactPerson.length > 0) {
      setSelectedContactIndex(0);
    }
  }, [yardDetail, hasAssignmentContact]);

  // Get current selected contact details
  const isAssignmentContactSelected = hasAssignmentContact && selectedContactIndex === -1;
  const currentContactPerson = isAssignmentContactSelected
    ? assignmentContact.person || "N/A"
    : yardDetail?.contactPerson?.[selectedContactIndex] || "N/A";
  const currentContactEmail = isAssignmentContactSelected
    ? assignmentContact.email || yardDetail?.email?.[0] || yardDetail?.contactEmail || "parts@ecauosalvage.com"
    : yardDetail?.email?.[selectedContactIndex] || yardDetail?.contactEmail || "parts@ecauosalvage.com";
  const currentContactNumber = isAssignmentContactSelected
    ? assignmentContact.number
    : yardDetail?.contactNumbers?.[selectedContactIndex] || "";

  const totalPrice = latestYard.finalPrice || latestYard.price || 0;
  const hasShipping =
    latestYard.yardShipping > 0 || latestYard.ownShipping > 0;
  const ownShipping = Number(latestYard.ownShipping) || 0;
  const formatAssignmentValue = (value) => {
    const normalized = value == null ? "" : String(value).trim();
    return normalized || "N/A";
  };

  const orderDateStr = lead.orderDate
    ? new Date(lead.orderDate).toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "";

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .email-chrome { box-shadow: none !important; }
        }
      `}</style>

      {/* ── Action bar (hidden on print) ── */}
      <div
        className="no-print"
        style={{
          background: "#f0f0f0",
          padding: "10px 16px",
          display: "flex",
          gap: 10,
          fontFamily: "Arial, sans-serif",
          fontSize: 13,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {(hasAssignmentContact || (yardDetail && yardDetail.contactPerson && yardDetail.contactPerson.length > 0)) && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>ATTN:</span>
            <select
              value={selectedContactIndex}
              onChange={(e) => setSelectedContactIndex(parseInt(e.target.value))}
              style={{
                padding: "6px 12px",
                borderRadius: 20,
                border: "1.5px solid #d1d5db",
                background: "#fff",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                outline: "none",
              }}
            >
              {hasAssignmentContact && (
                <option value={-1}>
                  {assignmentContact.person || "Assignment Contact"}
                  {assignmentContact.email && ` - ${assignmentContact.email}`}
                </option>
              )}
              {yardDetail?.contactPerson?.map((person, index) => (
                <option key={index} value={index}>
                  {person} 
                  {yardDetail.email?.[index] && ` - ${yardDetail.email[index]}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          onClick={handleSendToMail}
          disabled={loading}
          style={{
            background: "#1f2937",
            color: "#fff",
            border: "none",
            padding: "7px 18px",
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
          }}
        >
          ✉ {loading ? "Sending..." : "EMAIL PO"}
        </button>
        <button
          onClick={() => window.print()}
          style={{
            background: "#374151",
            color: "#fff",
            border: "none",
            padding: "7px 18px",
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          🖨 Print / Save PDF
        </button>
        
        {/* Hidden file input */}
        <input
          id="attach-input"
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
        <button
          onClick={() => document.getElementById("attach-input").click()}
          style={{
            background: "#fff",
            color: "#374151",
            border: "1.5px solid #d1d5db",
            padding: "7px 18px",
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          📎 Attach Files
        </button>
      </div>

      {/* ── Gmail-style email wrapper ── */}
      <div
        style={{
          background: "#f0f0f0",
          minHeight: "100vh",
          padding: "0 0 40px 0",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div
          className="email-chrome"
          style={{
            maxWidth: 620,
            margin: "0 auto",
            background: "#fff",
            boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
          }}
        >
          {/* ── Subject ── */}
          <div
            style={{
              background: "#fff",
              padding: "14px 16px 10px",
              fontSize: 20,
              fontWeight: "normal",
              color: "#202124",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            PURCHASE OF {lead.vehicleInfo?.part}
          </div>

          {/* ── From / To / Date ── */}
          <div
            style={{
              background: "#fff",
              padding: "10px 16px",
              borderBottom: "1px solid #e0e0e0",
              fontSize: 12,
              color: "#444",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 3,
              }}
            >
              <div>
                <strong style={{ color: "#202124" }}>
                  Premium Used Auto Parts
                </strong>{" "}
                &lt;{companyInfo.email}&gt;
              </div>
              <div style={{ color: "#666", whiteSpace: "nowrap", fontSize: 12 }}>
                {orderDateStr || "Fri, Mar 13, 2026 at 11:11 AM"}
              </div>
            </div>
            <div style={{ color: "#666", fontSize: 12 }}>
              To: {currentContactEmail}
            </div>
          </div>

          {/* ── Email body ── */}
          <div
            style={{
              background: "#fff",
              padding: "20px 28px 24px",
              lineHeight: 1.65,
              fontSize: 13,
              color: "#000",
            }}
          >
            {/* Logo + sender header */}
            <table
              width="100%"
              cellPadding="0"
              cellSpacing="0"
              style={{ marginBottom: 18 }}
            >
              <tbody>
                <tr>
                  <td style={{ verticalAlign: "middle" }}>
                    <img
                      src={Logo}
                      alt="Premium Used Auto Parts"
                      style={{ height: 56, width: "auto", objectFit: "contain" }}
                    />
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      verticalAlign: "middle",
                      fontSize: 13,
                      color: "#222",
                    }}
                  >
                    Premium Used Auto Parts &lt;{companyInfo.email}&gt;
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ATTN */}
            <p style={{ marginBottom: 14 }}>
              <strong>
                ATTN - {currentContactPerson}
              
              </strong>
            </p>

            {/* Main note */}
            <p style={{ fontWeight: "bold", fontSize: 13.5, marginBottom: 20 }}>
              NOTE : PLEASE SHIP THE PART ONLY TO SHIPPING ADDRESS AS GIVEN
              BELOW. PLEASE SHIP IT BLIND WITHOUT ANY TAGS. IF THE PART WENT
              WITH TAG I NEED A FULL REFUND.
            </p>

            <br />

            {/* SHIPPING ADDRESS */}
            <p style={{ fontWeight: "bold", marginBottom: 6 }}>
              SHIPPING ADDRESS-
            </p>
            <p style={{ marginBottom: 18 }}>
              {lead.bussinessName && String(lead.bussinessName).trim() && (
                <>
                  Business Name: {lead.bussinessName}
                  <br />
                </>
              )}
              {lead.shippingAddress ? (
                  <>
                    {lead.shippingAddress.street && (
                      <>{lead.shippingAddress.street},<br /></>
                    )}
                    {lead.shippingAddress.city} {lead.shippingAddress.state}{" "}
                    {lead.shippingAddress.zip}
                  </>
                ) : (
                  <span style={{ color: "#666" }}>N/A</span>
                )}
            </p>

            {/* CUSTOMER INFORMATION */}
            <p style={{ fontWeight: "bold", marginBottom: 6 }}>
              CUSTOMER INFORMATION-
            </p>
            <p style={{ marginBottom: 18 }}>
              <strong>Name &nbsp;: {lead.personalInfo?.fullName || "N/A"}</strong>
              <br />
              <strong>Phone : {lead.personalInfo?.phoneNumber || "N/A"}</strong>
            </p>

            {/* BILLING ADDRESS */}
            <p style={{ fontWeight: "bold", marginBottom: 6 }}>
              BILLING ADDRESS-
            </p>
            <p style={{ marginBottom: 18 }}>
              Granite Bay, CA, 95746,USA
            </p>

            {/* PARTS ORDERED */}
            <p style={{ fontWeight: "bold", marginBottom: 6 }}>
              PARTS ORDERED
            </p>
            <p style={{ marginBottom: 18 }}>
              Quote #: {formatAssignmentValue(latestYard.quoteNumber)}
              <br />
              Stock #: {formatAssignmentValue(latestYard.stockNumber)}
              <br />
              Part Grade: {formatAssignmentValue(latestYard.partGrade)}
              <br />
              {lead.vehicleInfo?.year} {lead.vehicleInfo?.make}{" "}
              {lead.vehicleInfo?.model}
              <br />
              VIN: {lead.vehicleInfo?.vin || "N/A"}
              <br />
              Part: {lead.vehicleInfo?.part || "N/A"}
              <br />
              Part Spec: {lead.vehicleInfo?.specification || "N/A"}
              <br />
              Warranty: {lead.warranty || "6 MONTH WARRANTY"}
            </p>

            {/* PICS BEFORE SHIPPING */}
            <p style={{ marginBottom: 18 }}>
              <strong>PICS BEFORE SHIPPING TO </strong>
              <a
                href="tel:9165174633"
                style={{ color: "#1a0dab", textDecoration: "none" }}
              >
                (916) 517 4633
              </a>{" "}
              0r{" "}
              <a
                href="tel:9165302423"
                style={{ color: "#1a0dab", textDecoration: "none" }}
              >
                (916) 530-2423
              </a>{" "}
              or{" "}
              <a
                href={`mailto:${companyInfo.email}`}
                style={{ color: "#1a0dab", textDecoration: "none" }}
              >
                parts@
                <br />
                premiumusedautoparts.com
              </a>
            </p>

            {/* TOTAL */}
            <p style={{ fontWeight: "bold", fontSize: 13, marginBottom: 4 }}>
              OWN SHIPPING : ${ownShipping.toFixed(2)}
            </p>
            <p style={{ fontWeight: "bold", fontSize: 14, marginBottom: 6 }}>
              TOTAL :&nbsp;{" "}
              <span style={{ color: "#cc0000", fontSize: 15 }}>
                $ {(latestYard.finalPrice || 0).toFixed(2)}
              </span>
              &nbsp;&nbsp;( &nbsp;INCLUDING SHIPPING&nbsp; )
            </p>

            <br />

            {/* TAX ID */}
            <p style={{ fontSize: 13, marginBottom: 14 }}>
              TAX ID :{" "}
              <a href="#" style={{ color: "#1a0dab" }}>
                214094368 - 00001
              </a>
            </p>

            <br />

            <p style={{ fontWeight: "bold", marginBottom: 10 }}>
              SEND ME AN INVOICE OF MY PURCHASE &amp;
              <br />
              UPDATE ME WITH THE TRACKING INFORMATION AS SOON AS POSSIBLE.
            </p>

            <p style={{ fontWeight: "bold", marginBottom: 10 }}>
              WE HAVE ATTACHED THE CREDIT CARD &nbsp;COPY AND DRIVER'S LICENSE
            </p>

            <p style={{ fontWeight: "bold", marginBottom: 10 }}>
              NOTE-PLEASE DO BLINDSHIP THE PART AND SHIP IT AS SOON AS
              POSSIBLE.
              <br />
              MAKE SURE IT DOESN'T HAVE ANY DEFECTS OR DAMAGES ON IT.
            </p>

            <p style={{ fontWeight: "bold", marginBottom: 10 }}>
              THANKS FOR YOUR BUSINESS!
            </p>

            <p style={{ fontWeight: "bold", marginBottom: 16 }}>
              {footerSignatoryName}
            </p>

            <hr
              style={{ border: "none", borderTop: "1px solid #ddd", margin: "16px 0" }}
            />

            {/* ── Signature block ── */}
            <div style={{ fontSize: 12, color: "#444" }}>
              <p style={{ marginBottom: 10 }}>--</p>

              {/* Logo */}
              <div style={{ marginBottom: 10 }}>
                <img
                  src={Logo}
                  alt="Premium Used Auto Parts"
                  style={{
                    height: 72,
                    width: 72,
                    objectFit: "contain",
                    borderRadius: "50%",
                  }}
                />
              </div>

              {/* Payment icons */}
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                {/* Mastercard */}
                <div
                  style={{
                    width: 42,
                    height: 26,
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <svg width="34" height="22" viewBox="0 0 34 22">
                    <circle cx="13" cy="11" r="9" fill="#eb001b" />
                    <circle cx="21" cy="11" r="9" fill="#f79e1b" />
                    <path
                      d="M17 4.8a9 9 0 0 1 0 12.4A9 9 0 0 1 17 4.8z"
                      fill="#ff5f00"
                    />
                  </svg>
                </div>
                {/* VISA */}
                <div
                  style={{
                    width: 42,
                    height: 26,
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Times New Roman', serif",
                      fontWeight: 900,
                      fontSize: 14,
                      color: "#1a1f71",
                      letterSpacing: -1,
                    }}
                  >
                    VISA
                  </span>
                </div>
                {/* Amex */}
                <div
                  style={{
                    width: 42,
                    height: 26,
                    borderRadius: 4,
                    background: "#2671b8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      color: "#fff",
                      fontSize: 7,
                      fontWeight: "bold",
                      textAlign: "center",
                      lineHeight: 1.1,
                    }}
                  >
                    AMERICAN
                    <br />
                    EXPRESS
                  </span>
                </div>
                {/* Discover */}
                <div
                  style={{
                    width: 42,
                    height: 26,
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <span
                    style={{
                      fontSize: 7,
                      fontWeight: "bold",
                      color: "#231f20",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    DISCOVER
                  </span>
                  <div
                    style={{
                      position: "absolute",
                      right: -4,
                      bottom: -4,
                      width: 22,
                      height: 22,
                      background: "#e65c00",
                      borderRadius: "50%",
                      opacity: 0.85,
                    }}
                  />
                </div>
              </div>

              {/* Company info */}
              <div style={{ fontWeight: "bold", fontSize: 13, color: "#202124", marginBottom: 3 }}>
                Premium Used Auto Parts
              </div>
              <div style={{ fontSize: 12, color: "#444", lineHeight: 1.6 }}>
                {companyInfo.cityStateZip}
                <br />
                {companyInfo.phone}
                <br />
                <a
                  href={`https://${companyInfo.website}`}
                  style={{ color: "#1a0dab", textDecoration: "none" }}
                >
                  {companyInfo.website}
                </a>
              </div>
            </div>
          </div>

          {/* ── Gmail-style Attachments Panel ── */}
          {attachments.length > 0 && (
            <div
              style={{
                borderTop: "1px solid #e0e0e0",
                padding: "12px 16px 16px",
                background: "#fff",
              }}
            >
              {/* Header */}
              <div
                style={{
                  fontSize: 12,
                  color: "#5f6368",
                  marginBottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>📎</span>
                <span style={{ fontWeight: 600 }}>
                  {attachments.length} Attachment{attachments.length > 1 ? "s" : ""}
                </span>
              </div>

              {/* Tiles */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {attachments.map((att) => (
                  <div
                    key={att.id}
                    style={{
                      width: 160,
                      border: "1px solid #dadce0",
                      borderRadius: 8,
                      overflow: "hidden",
                      background: "#f8f9fa",
                      position: "relative",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    }}
                  >
                    {/* Remove ✕ */}
                    <button
                      onClick={() => removeAttachment(att.id)}
                      title="Remove"
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        background: "rgba(0,0,0,0.45)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: 18,
                        height: 18,
                        fontSize: 10,
                        cursor: "pointer",
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                    >
                      ✕
                    </button>

                    {/* Preview */}
                    {att.previewUrl ? (
                      <div style={{ height: 90, overflow: "hidden" }}>
                        <img
                          src={att.previewUrl}
                          alt={att.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          height: 90,
                          background: "#e8eaed",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 34,
                        }}
                      >
                        {getFileIcon(att.type, att.name)}
                      </div>
                    )}

                    {/* File info */}
                    <div style={{ padding: "8px 10px 9px" }}>
                      <div
                        title={att.name}
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#202124",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          marginBottom: 2,
                        }}
                      >
                        {att.name}
                      </div>
                      <div style={{ fontSize: 10, color: "#80868b" }}>
                        {formatFileSize(att.size)}
                      </div>
                    </div>
                  </div>
                ))}

                {/* + Add more tile */}
                <div
                  onClick={() => document.getElementById("attach-input").click()}
                  style={{
                    width: 160,
                    height: 138,
                    border: "1.5px dashed #dadce0",
                    borderRadius: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    cursor: "pointer",
                    color: "#5f6368",
                    fontSize: 11,
                    fontWeight: 600,
                    background: "#fff",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#1a73e8";
                    e.currentTarget.style.background = "#f0f6ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#dadce0";
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  <span style={{ fontSize: 22 }}>＋</span>
                  Add more
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
